import React, { useState } from 'react';
import { Plus, Minus } from "lucide-react"; 
import { motion, AnimatePresence } from 'framer-motion';

const faqs = [
  {
    question: "Is this really free?",
    answer: "Yes. No fees, no deposits. We take a small equity/revenue share."
  },
  {
    question: "Do I need to be in Noida physically?",
    answer: "Not necessarily. We operate with a hybrid model, though physical presence helps during the build phase."
  },
  {
    question: "Do I need a full team?",
    answer: "No. You can start solo. We help you find co-founders and build the initial tech team."
  }
];

const FAQItem = ({ question, answer, isOpen, onClick, index }) => {
  return (
    <motion.div 
      // Staggered Entrance Animation
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, delay: index * 0.15, ease: "easeOut" }}
      onClick={onClick}
      className={`bg-white rounded-xl shadow-lg cursor-pointer transition-colors duration-300 border border-[#141414]/40 overflow-hidden ${isOpen ? 'shadow-2xl ring-2 ring-white/20' : 'hover:shadow-xl'}`}
    >
      <div className={`p-6 md:p-8 ${isOpen ? 'pb-2' : ''} flex items-center justify-between gap-4`}>
        <h3 className="font-[500] text-[#111827] text-[18px] md:text-[20px] select-none leading-tight">
          {question}
        </h3>
        {/* Icon Container with Rotation Animation */}
        <motion.span 
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.3, type: "spring", stiffness: 200 }}
          className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white bg-[#00C2FF]`}
        >
          {isOpen ? <Minus size={16} strokeWidth={3} /> : <Plus size={16} strokeWidth={3} />}
        </motion.span>
      </div>
      
      {/* Answer Section with Smooth Height Animation */}
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="answer"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            // Custom cubic-bezier for a "snappy" feel
            transition={{ duration: 0.4, ease: [0.04, 0.62, 0.23, 0.98] }} 
          >
            <div className="px-6 md:px-8 pb-8">
              <p className="text-[#0993D3] font-[500] text-[18px] md:text-[20px] leading-relaxed">
                - {answer}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(0); // Default: First item open

  const toggleFAQ = (index) => {
    // If clicked index is already open, close it (-1). Otherwise, open the new index.
    setOpenIndex(openIndex === index ? -1 : index);
  };

  return (
    // Main Container with Blue Gradient Background
    <section className="w-full bg-gradient-to-b from-[#009EEB] to-[#040E56] py-20">
      <div className="container max-w-7xl mx-auto px-6 md:px-12">
        {/* Heading Animation */}
        <motion.h2 
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-white font-[700] text-[28px] md:text-[38px] mb-10 text-left"
        >
          Frequently Asked Questions (FAQ)
        </motion.h2>
        
        {/* FAQ List */}
        <div className="flex flex-col gap-5">
          {faqs.map((faq, index) => (
            <FAQItem 
              key={index}
              index={index}
              question={faq.question}
              answer={faq.answer}
              isOpen={openIndex === index}
              onClick={() => toggleFAQ(index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;